import { useEffect, useMemo } from 'react';

import { LocalStore } from 'constants/localstore';
import { FileStatus } from 'services/file.service';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import {
  setSelectedProject,
  setSelectedFinalcut,
  setSelectedStory,
  setSelectedFile,
  setSelectedFolder,
} from 'components/app/projects/_mediator/projects.slice';
import { getDefaultProject, getDefaultStory, isNull } from 'components/app/_helpers/_app.helper';
import { ProjectLocalstore } from 'components/app/_mediator/localstore/hooks/use-project';
import { setAppRestored } from 'components/app/_mediator/app.slice';
import { UNFILED_FOLDER_ID } from 'components/app/projects/_mediator/projects.params';

export const useAppRestore = () => {
  const appRestored = useAppSelector((state) => state.app.appRestored);
  const projectLoaded = useAppSelector((state) => state.app.projectLoaded);
  const storyLoaded = useAppSelector((state) => state.app.storyLoaded);
  const finalcuts = useAppSelector((state) => state.app__data.finalcuts);
  const folders = useAppSelector((state) => state.app__data.folders);
  const projects = useAppSelector((state) => state.app__data.projects);
  const stories = useAppSelector((state) => state.app__data.stories);
  const fileStatuses = useAppSelector((state) => state.app__files.statuses);
  const dispatch = useAppDispatch();

  const data = useMemo(() => {
    const json = localStorage.getItem(LocalStore.project);
    if (json) return JSON.parse(json) as ProjectLocalstore;
  }, []);

  /* step 0 */

  useEffect(() => {
    if (appRestored) return;
    if (!folders) return;

    if (!data || !data.folderId) {
      dispatch(setSelectedFolder(UNFILED_FOLDER_ID));
      return;
    }
    dispatch(setSelectedFolder(data.folderId));
  }, [folders]);

  /* step 1 */

  useEffect(() => {
    if (appRestored) return;
    if (!projects) return;

    if (projects.length === 0) {
      dispatch(setAppRestored(true));
      return;
    }
    if (!data || isNull(data.projectId)) {
      dispatch(setSelectedProject(getDefaultProject(projects)));
      return;
    }
    if (!projects.find((project) => project.projectId === data.projectId)) {
      dispatch(setAppRestored(true));
      return;
    }
    dispatch(setSelectedProject(data.projectId));
  }, [projects]);

  /* step 2 */

  useEffect(() => {
    if (appRestored) return;
    if (!projectLoaded) return;

    if (!stories) {
      dispatch(setAppRestored(true));
      return;
    }
    const storyId = data?.storyId ?? getDefaultStory(stories);
    if (storyId) {
      const story = stories.find((story) => story.storyId === storyId);
      if (story) dispatch(setSelectedStory(storyId));
      else dispatch(setAppRestored(true));
    } else dispatch(setAppRestored(true));

    const fileId = data?.fileId;
    if (fileId) {
      const status = fileStatuses[fileId];
      if (status === FileStatus.Available) dispatch(setSelectedFile(fileId));
    }
  }, [projectLoaded]);

  /* step 3 */

  useEffect(() => {
    if (appRestored) return;
    if (!storyLoaded) return;
    if (data?.finalcutId) {
      const finalcut = finalcuts.find((finalcut) => finalcut.id === data.finalcutId);
      if (finalcut) dispatch(setSelectedFinalcut(data.finalcutId));
    }
    dispatch(setAppRestored(true));
  }, [storyLoaded]);
};
